import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { apiRequest, apiRequestFile } from "../utils/api";
import { useAuthContext } from "../context/AuthContext";
import { useChatUnread } from "../context/ChatUnreadContext";
import "../styles/GlobalChatPanel.css";

const GlobalChatPanel = () => {
  const { user: userInfo, loading: authLoading } = useAuthContext();
  const isAuthenticated = !!userInfo?.id;

  // =========================
  // state
  // =========================
  const [messages, setMessages] = useState([]);
  const [stamps, setStamps] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [showStampPicker, setShowStampPicker] = useState(false);
  const { unreadCount, refreshUnread } = useChatUnread();
  const [replyTarget, setReplyTarget] = useState(null);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showMessageMenu, setShowMessageMenu] = useState(false);
  const [selectedImages, setSelectedImages] = useState([]);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [isChatActive, setIsChatActive] = useState(false);

  // =========================
  // refs
  // =========================
  const pollingRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const initializedRef = useRef(false);
  const fetchNewMessagesControllerRef = useRef(null);
  const isFetchingNewMessagesRef = useRef(false);
  const lastMessageIdRef = useRef(null);

  // IntersectionObserver 用
  const messageElementRefs = useRef({});
  const observerRef = useRef(null);
  const pendingReadIdsRef = useRef(new Set());
  const flushReadTimerRef = useRef(null);

  const myUserId = userInfo?.id;

  const lastMessageId = useMemo(() => {
    if (!messages.length) return null;
    return messages[messages.length - 1].id;
  }, [messages]);

  useEffect(() => {
    lastMessageIdRef.current = lastMessageId;
  }, [lastMessageId]);

  // =========================
  // helper
  // =========================

  const stopPollingTimer = useCallback(() => {
    if (pollingRef.current) {
      clearTimeout(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  const abortFetchNewMessages = useCallback(() => {
    if (fetchNewMessagesControllerRef.current) {
      fetchNewMessagesControllerRef.current.abort();
      fetchNewMessagesControllerRef.current = null;
    }
    isFetchingNewMessagesRef.current = false;
  }, []);

  const isUnauthorizedError = useCallback((err) => {
    return (
      err?.status === 401 ||
      err?.response?.status === 401 ||
      err?.message?.includes?.("401") ||
      err?.message?.includes?.("Unauthorized") ||
      err?.message?.includes?.("再ログインが必要です")
    );
  }, []);

  const scrollToBottom = useCallback((smooth = true) => {
    const container = messagesContainerRef.current;
    if (!container) return;

    if (smooth) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    } else {
      container.scrollTop = container.scrollHeight;
    }
  }, []);

  const isNearBottom = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return true;

    return (
      container.scrollHeight - container.scrollTop - container.clientHeight < 80
    );
  }, []);

  const toMediaPath = useCallback((url) => {
    if (!url) return "";

    try {
      const parsed = new URL(url);
      return parsed.pathname;
    } catch {
      return url;
    }
  }, []);

  const formatDateLabel = useCallback((dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);

    return date.toLocaleDateString("ja-JP", {
      year: "numeric",
      month: "long",
      day: "numeric",
      weekday: "short",
    });
  }, []);

  const formatTimeLabel = useCallback((dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);

    return date.toLocaleTimeString("ja-JP", {
      hour: "2-digit",
      minute: "2-digit",
    });
  }, []);

  const isSameDate = useCallback((a, b) => {
    if (!a || !b) return false;

    const da = new Date(a);
    const db = new Date(b);

    return (
      da.getFullYear() === db.getFullYear() &&
      da.getMonth() === db.getMonth() &&
      da.getDate() === db.getDate()
    );
  }, []);

  const getReadLabel = useCallback((msg) => {
    const isMine =
      msg.isMine === true ||
      String(msg.user) === String(myUserId) ||
      String(msg.username) === String(userInfo?.username);

    if (isMine) {
      const count = Number(msg.read_count ?? 0);
      return count > 0 ? `既読 ${count}` : "未読";
    }

    return msg.is_read_by_me ? "既読" : "未読";
  }, [myUserId, userInfo?.username]);

  // =========================
  // API
  // =========================

  const fetchInitialMessages = useCallback(async () => {
    if (authLoading) return;

    if (!isAuthenticated) {
      setMessages([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError("");

      const data = await apiRequest("inventory/global-chat-messages/?limit=100");
      setMessages(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("チャット取得失敗:", err);

      if (isUnauthorizedError(err)) {
        setMessages([]);
        return;
      }

      setError("チャットの取得に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, isUnauthorizedError]);

  const fetchStamps = useCallback(async () => {
    if (authLoading) return;

    if (!isAuthenticated) {
      setStamps([]);
      return;
    }

    try {
      const data = await apiRequest("inventory/chat-stamps/");
      setStamps(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("スタンプ取得失敗:", err);

      if (isUnauthorizedError(err)) {
        setStamps([]);
      }
    }
  }, [authLoading, isAuthenticated, isUnauthorizedError]);

  const fetchNewMessages = useCallback(async () => {
    if (authLoading) return;
    if (!isAuthenticated) return;

    const currentLastMessageId = lastMessageIdRef.current;
    if (!currentLastMessageId) return;
    if (isFetchingNewMessagesRef.current) return;

    const controller = new AbortController();
    fetchNewMessagesControllerRef.current = controller;
    isFetchingNewMessagesRef.current = true;

    try {
      const data = await apiRequest(
        `inventory/global-chat-messages/?after_id=${currentLastMessageId}`,
        "GET",
        null,
        {},
        true,
        controller.signal
      );

      if (!Array.isArray(data) || data.length === 0) return;

      const shouldScroll = isNearBottom();

      const normalized = data.map((msg) => ({
        ...msg,
        isMine:
          String(msg.user) === String(myUserId) ||
          String(msg.username) === String(userInfo?.username),
      }));

      setMessages((prev) => {
        const existingIds = new Set(prev.map((msg) => msg.id));
        const newItems = normalized.filter((msg) => !existingIds.has(msg.id));
        if (newItems.length === 0) return prev;
        return [...prev, ...newItems];
      });

      refreshUnread();

      if (shouldScroll) {
        setTimeout(() => {
          scrollToBottom();
        }, 30);
      }
    } catch (err) {
      if (err.name === "AbortError") return;

      console.error("新着チャット取得失敗:", err);

      if (isUnauthorizedError(err)) {
        stopPollingTimer();
      }
    } finally {
      isFetchingNewMessagesRef.current = false;

      if (fetchNewMessagesControllerRef.current === controller) {
        fetchNewMessagesControllerRef.current = null;
      }
    }
  }, [
    authLoading,
    isAuthenticated,
    refreshUnread,
    isNearBottom,
    myUserId,
    userInfo?.username,
    scrollToBottom,
    stopPollingTimer,
    isUnauthorizedError,
  ]);

  // =========================
  // IntersectionObserver 既読処理
  // =========================

  const setMessageRef = useCallback((messageId, el) => {
    if (el) {
      messageElementRefs.current[messageId] = el;
    } else {
      delete messageElementRefs.current[messageId];
    }
  }, []);

  const flushReadQueue = useCallback(async () => {
    if (authLoading) return;
    if (!isAuthenticated) return;

    const ids = Array.from(pendingReadIdsRef.current);
    if (ids.length === 0) return;

    pendingReadIdsRef.current.clear();

    try {
      await apiRequest("inventory/global-chat-messages/mark_read_bulk/", "POST", {
        message_ids: ids,
      });

      refreshUnread();

      setMessages((prev) =>
        prev.map((msg) => {
          if (!ids.includes(msg.id)) return msg;

          const isMine =
            String(msg.user) === String(myUserId) ||
            String(msg.username) === String(userInfo?.username);

          return {
            ...msg,
            is_read_by_me: true,
            read_count: isMine
              ? Number(msg.read_count ?? 0) + 1
              : Number(msg.read_count ?? 0),
          };
        })
      );
    } catch (err) {
      console.error("既読更新失敗:", err);

      if (isUnauthorizedError(err)) {
        stopPollingTimer();
      }
    }
  }, [
    authLoading,
    isAuthenticated,
    refreshUnread,
    myUserId,
    userInfo?.username,
    isUnauthorizedError,
    stopPollingTimer,
  ]);

  const queueMarkAsRead = useCallback((messageId) => {
    if (!messageId) return;
    if (authLoading) return;
    if (!isAuthenticated) return;

    pendingReadIdsRef.current.add(messageId);

    if (flushReadTimerRef.current) {
      clearTimeout(flushReadTimerRef.current);
    }

    flushReadTimerRef.current = setTimeout(() => {
      flushReadQueue();
    }, 300);
  }, [authLoading, isAuthenticated, flushReadQueue]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) return;
    if (!messagesContainerRef.current) return;

    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          if (entry.intersectionRatio < 0.6) return;
          if (!isChatActive) return;
          if (document.visibilityState !== "visible") return;

          const messageId = Number(entry.target.dataset.messageId);
          if (!messageId) return;

          const targetMsg = messages.find((m) => m.id === messageId);
          if (!targetMsg) return;

          const isMine =
            String(targetMsg.user) === String(myUserId) ||
            String(targetMsg.username) === String(userInfo?.username);

          if (isMine) return;
          if (targetMsg.is_deleted) return;
          if (targetMsg.is_read_by_me) return;

          queueMarkAsRead(messageId);
        });
      },
      {
        root: messagesContainerRef.current,
        threshold: [0.6],
      }
    );

    const observer = observerRef.current;

    Object.values(messageElementRefs.current).forEach((el) => {
      if (el) observer.observe(el);
    });

    return () => {
      observer.disconnect();
    };
  }, [
    authLoading,
    isAuthenticated,
    messages,
    myUserId,
    userInfo?.username,
    queueMarkAsRead,
    isChatActive,
  ]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) return;
    if (!isChatActive) return;
    if (!observerRef.current) return;

    observerRef.current.disconnect();

    Object.values(messageElementRefs.current).forEach((el) => {
      if (el) observerRef.current.observe(el);
    });
  }, [authLoading, isAuthenticated, isChatActive, messages]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      if (authLoading) return;
      if (!isAuthenticated) return;
      if (!observerRef.current) return;

      observerRef.current.disconnect();

      Object.values(messageElementRefs.current).forEach((el) => {
        if (el) observerRef.current.observe(el);
      });
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    return () => {
      if (flushReadTimerRef.current) {
        clearTimeout(flushReadTimerRef.current);
      }
    };
  }, []);

  // =========================
  // 送信系
  // =========================

  const sendMessage = useCallback(async () => {
    if (authLoading) return;
    if (!isAuthenticated) return;

    const trimmed = input.trim();

    if ((!trimmed && selectedImages.length === 0) || sending) return;

    try {
      setSending(true);
      setError("");

      let created;

      if (selectedImages.length > 0) {
        created = await apiRequestFile(
          "inventory/global-chat-messages/",
          "POST",
          selectedImages,
          {
            message: trimmed || "",
            reply_to: replyTarget?.id ?? null,
          }
        );
      } else {
        const payload = {
          message: trimmed,
        };

        if (replyTarget?.id) {
          payload.reply_to = replyTarget.id;
        }

        created = await apiRequest("inventory/global-chat-messages/", "POST", payload);
      }

      setMessages((prev) => [...prev, { ...created, isMine: true }]);
      setInput("");
      setSelectedImages([]);
      setReplyTarget(null);

      setTimeout(() => {
        scrollToBottom();
      }, 30);
    } catch (err) {
      console.error("メッセージ送信失敗:", err);

      if (isUnauthorizedError(err)) {
        setError("ログイン状態を確認してください。");
        return;
      }

      setError("メッセージ送信に失敗しました。");
    } finally {
      setSending(false);
    }
  }, [
    authLoading,
    isAuthenticated,
    input,
    selectedImages,
    sending,
    replyTarget,
    scrollToBottom,
    isUnauthorizedError,
  ]);

  const sendStamp = useCallback(async (stampId) => {
    if (authLoading) return;
    if (!isAuthenticated) return;
    if (sending) return;

    try {
      setSending(true);
      setError("");

      const payload = { stamp: stampId };

      if (replyTarget?.id) {
        payload.reply_to = replyTarget.id;
      }

      const created = await apiRequest("inventory/global-chat-messages/", "POST", payload);

      setMessages((prev) => [...prev, { ...created, isMine: true }]);
      setShowStampPicker(false);
      setReplyTarget(null);

      setTimeout(() => {
        scrollToBottom();
      }, 30);
    } catch (err) {
      console.error("スタンプ送信失敗:", err);

      if (isUnauthorizedError(err)) {
        setError("ログイン状態を確認してください。");
        return;
      }

      setError("スタンプ送信に失敗しました。");
    } finally {
      setSending(false);
    }
  }, [
    authLoading,
    isAuthenticated,
    sending,
    replyTarget,
    scrollToBottom,
    isUnauthorizedError,
  ]);

  // =========================
  // メニュー系
  // =========================

  const openMessageMenu = useCallback((msg) => {
    setSelectedMessage(msg);
    setShowMessageMenu(true);
  }, []);

  const handleReplyFromMenu = useCallback(() => {
    if (!selectedMessage) return;

    setReplyTarget(selectedMessage);
    setShowMessageMenu(false);
    setSelectedMessage(null);
  }, [selectedMessage]);

  const deleteMessage = useCallback(async (messageId) => {
    if (authLoading) return;
    if (!isAuthenticated) return;
    if (!messageId) return;

    try {
      setError("");

      const updated = await apiRequest(
        `inventory/global-chat-messages/${messageId}/`,
        "DELETE"
      );

      setMessages((prev) =>
        prev.map((msg) => (msg.id === messageId ? updated : msg))
      );

      setShowMessageMenu(false);
      setSelectedMessage(null);
    } catch (err) {
      console.error("メッセージ削除失敗:", err);

      if (isUnauthorizedError(err)) {
        setError("ログイン状態を確認してください。");
        return;
      }

      setError("メッセージ削除に失敗しました。");
    }
  }, [authLoading, isAuthenticated, isUnauthorizedError]);

  const handleDeleteFromMenu = useCallback(() => {
    if (!selectedMessage) return;
    deleteMessage(selectedMessage.id);
  }, [selectedMessage, deleteMessage]);

  // =========================
  // 入力系
  // =========================

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage();
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setSelectedImages(files);
  };

  const removeSelectedImage = (indexToRemove) => {
    setSelectedImages((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  // =========================
  // 初期ロード
  // =========================

  useEffect(() => {
    if (authLoading) return;

    if (!isAuthenticated) {
      stopPollingTimer();
      abortFetchNewMessages();
      setMessages([]);
      setStamps([]);
      setLoading(false);
      return;
    }

    const init = async () => {
      await Promise.all([
        fetchInitialMessages(),
        fetchStamps(),
      ]);
    };

    init();
  }, [
    authLoading,
    isAuthenticated,
    fetchInitialMessages,
    fetchStamps,
    stopPollingTimer,
    abortFetchNewMessages,
  ]);

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) return;
    if (loading) return;
    if (!messages.length) return;
    if (initializedRef.current) return;

    initializedRef.current = true;

    scrollToBottom(false);

    const t1 = setTimeout(() => {
      scrollToBottom(false);
    }, 50);

    const t2 = setTimeout(() => {
      scrollToBottom(false);
    }, 200);

    const t3 = setTimeout(() => {
      scrollToBottom(false);
    }, 500);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
  }, [authLoading, isAuthenticated, loading, messages.length, scrollToBottom]);

  // 定期的に新着取得
  useEffect(() => {
    stopPollingTimer();

    if (authLoading) return;
    if (!isAuthenticated) return;

    let cancelled = false;

    const loop = async () => {
      if (cancelled) return;

      if (document.visibilityState !== "visible") {
        pollingRef.current = setTimeout(loop, 5000);
        return;
      }

      await fetchNewMessages();

      if (cancelled) return;
      pollingRef.current = setTimeout(loop, 5000);
    };

    loop();

    return () => {
      cancelled = true;
      stopPollingTimer();
    };
  }, [authLoading, isAuthenticated, fetchNewMessages, stopPollingTimer]);

  // unmount時だけ通信abort
  useEffect(() => {
    return () => {
      abortFetchNewMessages();
    };
  }, [abortFetchNewMessages]);

  useEffect(() => {
    const container = messagesContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setShowScrollToBottom(!isNearBottom());
    };

    container.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [isNearBottom]);

  // =========================
  // モーダル判定
  // =========================

  const isDeletedSelectedMessage =
    selectedMessage?.is_deleted === true ||
    selectedMessage?.is_deleted === "true";

  const canDeleteSelectedMessage =
    !!selectedMessage &&
    String(selectedMessage.user) === String(myUserId) &&
    !isDeletedSelectedMessage;

  return (
    <div className="global-chat-panel">
      <div
        className="global-chat-header"
        style={{ backgroundImage: `url(/media/system_images/chat_header.png)` }}
      >
        <div>
          <div className="global-chat-title">Chat</div>
        </div>
        <div className="global-chat-unread-badge">
          未読: {unreadCount}
        </div>
      </div>

      {error && <div className="global-chat-error">{error}</div>}

      <div
        className="global-chat-messages"
        style={{ backgroundImage: `url(/media/system_images/marin_chat.jpeg)` }}
        ref={messagesContainerRef}
        onMouseEnter={() => setIsChatActive(true)}
        onMouseLeave={() => setIsChatActive(false)}
      >
        {authLoading ? (
          <div className="global-chat-empty">認証確認中...</div>
        ) : loading ? (
          <div className="global-chat-empty">読み込み中...</div>
        ) : messages.length === 0 ? (
          <div className="global-chat-empty">まだメッセージはありません</div>
        ) : (
          messages.map((msg, index) => {
            const isMine =
              String(msg.user) === String(myUserId) ||
              String(msg.username) === String(userInfo?.username);

            const isMediaOnly =
              !msg.message?.trim() &&
              (msg.stamp_detail?.image || (msg.images && msg.images.length > 0));

            const prevMsg = index > 0 ? messages[index - 1] : null;
            const showDateDivider =
              !prevMsg || !isSameDate(prevMsg.created_at, msg.created_at);

            return (
              <React.Fragment key={msg.id}>
                {showDateDivider && (
                  <div className="global-chat-date-divider">
                    <span className="global-chat-date-divider-label">
                      {formatDateLabel(msg.created_at)}
                    </span>
                  </div>
                )}

                <div
                  className={`global-chat-row ${isMine ? "mine" : ""}`}
                  ref={(el) => setMessageRef(msg.id, el)}
                  data-message-id={msg.id}
                >
                  <div className={`global-chat-message-group ${isMine ? "mine" : ""}`}>
                    {!isMine && (
                      <div className="global-chat-username">
                        {msg.username || "unknown"}
                      </div>
                    )}

                    <div className="global-chat-message-inline">
                      <div
                        className={`global-chat-bubble ${isMine ? "mine" : ""} ${isMediaOnly ? "media-only" : ""}`}
                        onClick={() => openMessageMenu({ ...msg, isMine })}
                      >
                        {msg.reply_to_detail && (
                          <div className="global-chat-reply-preview">
                            <div className="global-chat-reply-user">
                              {msg.reply_to_detail.username}
                            </div>
                            <div className="global-chat-reply-text">
                              {msg.reply_to_detail.is_deleted
                                ? "削除されたメッセージ"
                                : msg.reply_to_detail.message || "スタンプ / 画像"}
                            </div>
                          </div>
                        )}

                        {msg.is_deleted ? (
                          <div className="global-chat-deleted">
                            このメッセージは削除されました
                          </div>
                        ) : (
                          <>
                            {msg.message && (
                              <div className="global-chat-text">{msg.message}</div>
                            )}

                            {msg.stamp_detail?.image && (
                              <div className="global-chat-stamp-wrap">
                                <img
                                  src={toMediaPath(msg.stamp_detail.image)}
                                  alt={msg.stamp_detail.name || "stamp"}
                                  className="global-chat-stamp"
                                />
                              </div>
                            )}

                            {Array.isArray(msg.images) && msg.images.length > 0 && (
                              <div className="global-chat-image-list">
                                {msg.images.map((image) => (
                                  <img
                                    key={image.id}
                                    src={toMediaPath(image.image)}
                                    alt=""
                                    className="global-chat-image"
                                  />
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>

                      <div className={`global-chat-side-meta ${isMine ? "mine" : "other"}`}>
                        <span className="global-chat-read-status">
                          {getReadLabel({ ...msg, isMine })}
                        </span>
                        <span className="global-chat-time">
                          {formatTimeLabel(msg.created_at)}
                        </span>
                        {msg.edited_at && (
                          <span className="global-chat-edited">編集済み</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}
      </div>

      {showScrollToBottom && (
        <button
          type="button"
          className="global-chat-scroll-bottom-button"
          onClick={() => {
            scrollToBottom();
            setShowScrollToBottom(false);
          }}
        >
          ↓
        </button>
      )}

      {showStampPicker && (
        <div className="global-chat-stamp-picker">
          {stamps.length === 0 ? (
            <div className="global-chat-stamp-empty">スタンプがありません</div>
          ) : (
            stamps.map((stamp) => (
              <button
                key={stamp.id}
                type="button"
                className="global-chat-stamp-button"
                onClick={() => sendStamp(stamp.id)}
              >
                <img
                  src={toMediaPath(stamp.image)}
                  alt={stamp.name}
                />
              </button>
            ))
          )}
        </div>
      )}

      {selectedImages.length > 0 && (
        <div className="global-chat-selected-images">
          {selectedImages.map((file, index) => (
            <div key={`${file.name}-${index}`} className="global-chat-selected-image-item">
              <img
                src={URL.createObjectURL(file)}
                alt=""
                className="global-chat-selected-image-preview"
              />
              <button
                type="button"
                className="global-chat-selected-image-remove"
                onClick={() => removeSelectedImage(index)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <form className="global-chat-input-area" onSubmit={handleSubmit}>
        <label className="global-chat-file-button">
          画像
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageChange}
            style={{ display: "none" }}
          />
        </label>

        <div className="global-chat-input-wrapper">
          <textarea
            className="global-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="メッセージを入力"
            rows={2}
          />
          <button
            type="button"
            className="global-chat-stamp-icon"
            onClick={() => setShowStampPicker((prev) => !prev)}
          >
            💗
          </button>
        </div>

        <button
          type="submit"
          className="global-chat-send-button"
          disabled={sending || authLoading || !isAuthenticated}
        >
          {sending ? "܀ꕤ୭" : "✿"}
        </button>
      </form>

      {showMessageMenu && selectedMessage && (
        <div
          className="global-chat-menu-overlay"
          onClick={() => {
            setShowMessageMenu(false);
            setSelectedMessage(null);
          }}
        >
          <div
            className="global-chat-menu-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="global-chat-menu-title">メニュー</div>

            {!isDeletedSelectedMessage && (
              <button
                type="button"
                className="global-chat-menu-button"
                onClick={handleReplyFromMenu}
              >
                返信
              </button>
            )}

            {canDeleteSelectedMessage && (
              <button
                type="button"
                className="global-chat-menu-button delete"
                onClick={handleDeleteFromMenu}
              >
                削除
              </button>
            )}

            <button
              type="button"
              className="global-chat-menu-button cancel"
              onClick={() => {
                setShowMessageMenu(false);
                setSelectedMessage(null);
              }}
            >
              閉じる
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GlobalChatPanel;